import { createAuthClient } from 'better-auth/react'
import { adminClient, apiKeyClient, organizationClient } from 'better-auth/client/plugins'
import { polarClient } from '@polar-sh/better-auth/client'

// Lazy-initialize the auth client to avoid SSR issues in Cloudflare Workers
// The client is only created when first accessed on the client side
let _authClient: ReturnType<typeof createAuthClient> | null = null

function getAuthClient() {
  if (_authClient) return _authClient

  // Only create the client with a valid baseURL on the client side
  const baseURL =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'http://localhost:3000'

  _authClient = createAuthClient({
    baseURL,
    plugins: [adminClient(), apiKeyClient(), organizationClient(), polarClient()],
  })

  return _authClient
}

// Export a proxy that lazily initializes the client
export const authClient = new Proxy({} as ReturnType<typeof createAuthClient>, {
  get(_, prop) {
    return getAuthClient()[prop as keyof ReturnType<typeof createAuthClient>]
  },
})

// Export commonly used methods using proxy to preserve nested methods (e.g., signIn.social, signIn.email)
export const signIn = new Proxy({} as typeof authClient.signIn, {
  get(_, prop) {
    return (getAuthClient().signIn as any)[prop]
  },
  apply(_, __, args) {
    return (getAuthClient().signIn as any)(...args)
  },
})

export const signUp = new Proxy({} as typeof authClient.signUp, {
  get(_, prop) {
    return (getAuthClient().signUp as any)[prop]
  },
  apply(_, __, args) {
    return (getAuthClient().signUp as any)(...args)
  },
})

export const signOut: typeof authClient.signOut = (...args) =>
  getAuthClient().signOut(...args)
export const useSession: typeof authClient.useSession = (...args) =>
  getAuthClient().useSession(...args)
export const getSession: typeof authClient.getSession = (...args) =>
  getAuthClient().getSession(...args)
export const resetPassword = ((...args: Array<any>) =>
  (getAuthClient().resetPassword as any)(
    ...args,
  )) as typeof authClient.resetPassword

// Export API Key methods from the apiKey plugin
export const apiKey = new Proxy({} as any, {
  get(_, prop) {
    const client = getAuthClient() as any
    return client.apiKey?.[prop]
  },
})

// Export Polar checkout method
export const polarCheckout = (...args: Array<any>) =>
  (getAuthClient() as any).checkout?.(...args)

// Export Polar customer methods (portal, state, subscriptions, orders, benefits)
export const customer = new Proxy({} as any, {
  get(_, prop) {
    const client = getAuthClient() as any
    return client.customer?.[prop]
  },
})

// Export Admin methods from the admin plugin
export const admin = new Proxy({} as any, {
  get(_, prop) {
    const client = getAuthClient() as any
    return client.admin?.[prop]
  },
})

// Export Organization methods from the organization plugin
export const organization = new Proxy({} as any, {
  get(_, prop) {
    const client = getAuthClient() as any
    return client.organization?.[prop]
  },
})
